'use client';
import React, { useEffect, useRef, useState } from 'react';

const services = [
  {
    id: 1,
    title: 'Veneers',
    description:
      'Transform your smile with ultra-thin, custom-crafted porcelain veneers. Correct chips, stains, gaps, and misalignment for a flawless, natural-looking result.',
    items: ['Porcelain Veneers', 'Composite Veneers', 'Smile Makeover', 'Stain Correction'],
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_196ce7eb1-1772074804611.png',
    alt: 'Close-up of beautiful white teeth veneers dental procedure',
  },
  {
    id: 2,
    title: 'Smile Design',
    description:
      'A comprehensive aesthetic approach to creating your ideal smile. We analyze facial features, tooth proportions, and your vision to design a smile uniquely yours.',
    items: ['Digital Smile Design', 'Aesthetic Analysis', 'Mock-up Preview', 'Full Smile Makeover'],
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b3bb7cc7-1774495347429.png',
    alt: 'Patient showing beautiful smile after smile design treatment',
  },
  {
    id: 3,
    title: 'Full Mouth Rehabilitation',
    description:
      'Comprehensive restoration of all teeth to rebuild function, health, and aesthetics. Ideal for patients with extensive wear, decay, or bite problems.',
    items: ['Complete Oral Restoration', 'Bite Correction', 'Crown & Bridge', 'Occlusal Rehabilitation'],
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_151fe4e30-1778062073278.png',
    alt: 'Dental professional performing full mouth rehabilitation procedure',
  },
  {
    id: 4,
    title: 'Full Mouth Implant Restoration',
    description:
      'Replace missing teeth with permanent, natural-looking implants. From single implants to full arch restorations, regain your smile and confidence.',
    items: ['Dental Implants', 'All-on-4 / All-on-6', 'Implant Crowns', 'Bone Grafting'],
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_19baea130-1773141435648.png',
    alt: 'Dental implant model showing full mouth implant restoration',
  },
];

export default function Services() {
  const [visible, setVisible] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="bg-secondary py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div
              className={`inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 mb-6 transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <span className="font-inter text-xs font-semibold text-dark/60 uppercase tracking-widest">Our Services</span>
            </div>
            <h2
              className={`font-unbounded font-black text-dark text-3xl lg:text-4xl xl:text-5xl leading-[1.08] transition-all duration-600 delay-100 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              Dental Services
              <br />
              for Every Need
            </h2>
          </div>
          <div
            className={`lg:max-w-xs transition-all duration-600 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="font-inter text-dark/55 text-sm leading-relaxed mb-5">
              From cosmetic enhancements to complete restorative treatments, we provide a full range of
              dental services tailored to your needs.
            </p>
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 bg-dark text-white font-inter font-semibold text-sm px-6 py-3 rounded-full hover:bg-dark-light transition-all duration-200 hover:shadow-card"
            >
              Book a Consultation
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Service Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services?.map((service, i) => (
            <div
              key={service?.id}
              className={`bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-card-hover border-2 ${
                activeService === i ? 'border-primary shadow-teal' : 'border-transparent'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 80}ms` }}
              onClick={() => setActiveService(i)}
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden bg-secondary">
                <img
                  src={service?.image}
                  alt={service?.alt}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark/15 to-transparent" />
                {activeService === i && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="#131c15" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-unbounded font-bold text-dark text-sm mb-2">{service?.title}</h3>
                <p className="font-inter text-dark/55 text-xs leading-relaxed mb-4">
                  {service?.description?.substring(0, 75)}...
                </p>
                <div className="space-y-1.5 mb-4">
                  {service?.items?.slice(0, 3)?.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-dark/40" />
                      <span className="font-inter text-xs text-dark/65">{item}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    const el = document.querySelector('#contact');
                    if (el) el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center gap-1.5 font-inter text-xs font-semibold text-dark hover:gap-3 transition-all duration-200"
                >
                  Explore more
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Active Service Detail */}
        <div
          className={`mt-6 bg-white rounded-3xl p-8 lg:p-10 shadow-card transition-all duration-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 bg-primary/30 text-dark font-inter text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
                <div className="w-1.5 h-1.5 bg-dark rounded-full" />
                Featured Service
              </span>
              <h3 className="font-unbounded font-black text-dark text-2xl lg:text-3xl mb-4">
                {services?.[activeService]?.title}
              </h3>
              <p className="font-inter text-dark/55 text-sm lg:text-base leading-relaxed mb-6">
                {services?.[activeService]?.description}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {services?.[activeService]?.items?.map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="#131c15" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className="font-inter text-xs font-medium text-dark">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-64 lg:h-80 shadow-card">
              <img
                src={services?.[activeService]?.image}
                alt={services?.[activeService]?.alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
